/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Handles login, logout, registration, and token management.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, RegisterRequest, AuthState } from '../types/auth';
import { authApi, tokenService } from '../services/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    accessToken: null,
    refreshToken: null,
  });

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = tokenService.getAccessToken();
      const refreshToken = tokenService.getRefreshToken();
      const user = tokenService.getUser();

      if (accessToken && user) {
        // Verify token is still valid
        const isValid = await authApi.verifyToken();

        if (isValid) {
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessToken,
            refreshToken,
          });
        } else {
          // Token invalid, try to refresh
          try {
            await authApi.refreshToken();
            const updatedUser = await authApi.getCurrentUser();
            setState({
              user: updatedUser,
              isAuthenticated: true,
              isLoading: false,
              accessToken: tokenService.getAccessToken(),
              refreshToken: tokenService.getRefreshToken(),
            });
          } catch (error) {
            // Refresh failed, clear auth
            tokenService.clearTokens();
            setState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              accessToken: null,
              refreshToken: null,
            });
          }
        }
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          accessToken: null,
          refreshToken: null,
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login user
   */
  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      const response = await authApi.login(credentials);

      setState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      });
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  /**
   * Register new user
   */
  const register = async (data: RegisterRequest): Promise<void> => {
    try {
      await authApi.register(data);
      // After successful registration, user needs to login
      // We don't automatically log them in for security
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  };

  /**
   * Logout user
   */
  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state even if API call fails
      tokenService.clearTokens();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        accessToken: null,
        refreshToken: null,
      });
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUserData = async (): Promise<void> => {
    try {
      const user = await authApi.getCurrentUser();
      tokenService.setUser(user);
      setState((prev) => ({
        ...prev,
        user,
      }));
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  /**
   * Check if user is authenticated
   */
  const checkAuth = async (): Promise<boolean> => {
    const accessToken = tokenService.getAccessToken();
    if (!accessToken) {
      return false;
    }

    try {
      const isValid = await authApi.verifyToken();
      return isValid;
    } catch (error) {
      return false;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    refreshUserData,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
